import React, { useEffect, useRef, useState } from 'react';
import { uploadFiles } from '../../services/upload.service';
import { resolveFileUrl } from '../../utils/fileUrl';

/**
 * Lightweight WYSIWYG editor for letter templates.
 *
 * Letter contents are stored as HTML; a plain textarea forces users to read
 * raw tags. This renders the letter formatted, offers basic formatting
 * controls, and one-click insertion of the template placeholders.
 */

export interface PlaceholderOption {
  token: string;
  label: string;
}

interface RichTextEditorProps {
  label: string;
  value: string;
  onChange: (html: string) => void;
  error?: string;
  placeholders?: PlaceholderOption[];
  dir?: 'rtl' | 'ltr';
  minHeight?: number;
}

const toolbarButtons: { command: string; title: string; content: React.ReactNode }[] = [
  { command: 'bold', title: 'Bold (غامق)', content: <strong>B</strong> },
  { command: 'italic', title: 'Italic (مائل)', content: <em>I</em> },
  { command: 'underline', title: 'Underline (تسطير)', content: <span className="underline">U</span> },
  { command: 'justifyRight', title: 'Align right (محاذاة يمين)', content: <span>⇥</span> },
  { command: 'justifyCenter', title: 'Center (توسيط)', content: <span>↔</span> },
  { command: 'justifyLeft', title: 'Align left (محاذاة يسار)', content: <span>⇤</span> },
  { command: 'insertUnorderedList', title: 'Bullet list (قائمة)', content: <span>••</span> },
  { command: 'undo', title: 'Undo (تراجع)', content: <span>↺</span> },
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  error,
  placeholders = [],
  dir = 'rtl',
  minHeight = 280,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Sync external value into the editor, but never while the user is typing
  // (rewriting innerHTML mid-edit would reset the caret position).
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const exec = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command);
    emitChange();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedRangeRef.current);
  };

  const insertImageFile = async (file: File) => {
    try {
      setIsUploadingImage(true);
      const [info] = await uploadFiles([file]);
      const url = resolveFileUrl(info?.url);
      if (url && editorRef.current) {
        editorRef.current.focus();
        restoreSelection();
        document.execCommand('insertHTML', false, `<img src="${url}" alt="" style="max-height: 100px; max-width: 100%;" />`);
        emitChange();
      }
    } catch {
      // Upload failure surfaces via console; the letter simply keeps no image.
      console.error('Image upload failed');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const imageFile = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
    if (!imageFile) return; // regular text/html paste keeps the default behavior
    e.preventDefault();
    saveSelection();
    insertImageFile(imageFile);
  };

  const insertPlaceholder = (token: string) => {
    editorRef.current?.focus();
    document.execCommand('insertText', false, token);
    emitChange();
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      <div className={`border rounded-md ${error ? 'border-red-400' : 'border-gray-300'}`}>
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5 rounded-t-md">
          {toolbarButtons.map((btn) => (
            <button
              key={btn.command}
              type="button"
              title={btn.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec(btn.command)}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 text-gray-700 text-sm"
            >
              {btn.content}
            </button>
          ))}
          <button
            type="button"
            title={isUploadingImage ? 'Uploading image… (جاري رفع الصورة)' : 'Insert image (إدراج صورة)'}
            disabled={isUploadingImage}
            onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
            onClick={() => fileInputRef.current?.click()}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 text-gray-700 text-sm disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) insertImageFile(file);
              e.target.value = '';
            }}
          />
          {placeholders.length > 0 && <span className="mx-1 h-5 w-px bg-gray-300" />}
          {placeholders.map((p) => (
            <button
              key={p.token}
              type="button"
              title={p.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertPlaceholder(p.token)}
              className="px-2 h-8 flex items-center rounded bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div
          ref={editorRef}
          contentEditable
          dir={dir}
          onInput={emitChange}
          onBlur={emitChange}
          onPaste={handlePaste}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          className="px-4 py-3 focus:outline-none text-sm leading-8 text-gray-900 rounded-b-md overflow-y-auto"
          style={{ minHeight, maxHeight: 420 }}
          suppressContentEditableWarning
        />
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default RichTextEditor;
